import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/App";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("Something went wrong. Try again.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 islamic-pattern">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-gold-400 font-outfit mb-6"
          data-testid="back-to-login-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>

        <div className="glass-card rounded-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="/hifz-logo.png"
              alt="Hifz"
              className="w-20 h-20 rounded-full mx-auto mb-4 shadow-lg"
            />
            <h1 className="font-cinzel text-2xl font-bold text-slate-100">Forgot password?</h1>
            <p className="text-slate-400 font-outfit text-sm mt-2">
              Enter your email and we'll send a secure reset link.
            </p>
          </div>

          {submitted ? (
            <div
              data-testid="forgot-success-card"
              className="text-center space-y-4"
            >
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="text-slate-200 font-outfit">
                If that email is registered, we've sent a reset link. It expires in an hour.
              </p>
              <p className="text-slate-500 text-xs font-outfit">
                Check your spam folder if you don't see it.
              </p>
              <Link
                to="/login"
                className="inline-block text-gold-400 hover:text-gold-300 font-outfit text-sm"
              >
                Return to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-300 font-outfit">
                  Email
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10 bg-islamic-blue-950/50 border-white/10 text-slate-100 placeholder:text-slate-600"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="send-reset-link-btn"
                className="w-full btn-gold rounded-full py-6 font-outfit"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
